import React, { useContext, useEffect, useRef, useState } from 'react';
import merge from 'lodash/merge';
import { ForgePopover } from '@tylertech/forge-react';
import { isInsideFlannels } from '../lib/helpers';
import { SoQLType } from 'common/types/soql';
import { ParameterItemContext } from './ParameterItemContext';
import { ParameterControl } from './ParameterItemControl';
import { ParameterControlPopover } from './ParameterItemControlPopover';
import type { ParameterConfiguration, DataSourceCCVs } from 'common/types/reportFilters';
import type { FilterBarPendoIds } from '../types';

function ParameterItemCore({ pendoIds }: Pick<ParamItemProps, 'pendoIds'>) {
  const { parameter, onParameterUpdate, open, setOpen, textInputRef, toggleRef } =
    useContext(ParameterItemContext)!;

  useEffect(() => {
    // Avoid closing flannels if the click is inside any of these refs.
    const bodyClickHandler = (event: MouseEvent) => {
      const flannelElements = [textInputRef.current, toggleRef.current];

      // If none of the flannelElements contain event.target, close all the flannels.
      if (!isInsideFlannels(event, flannelElements)) {
        setOpen(false);
      }
    };

    document.body.addEventListener('click', bodyClickHandler);

    return () => {
      document.body.removeEventListener('click', bodyClickHandler);
    };
  }, []);

  const onUpdate = (parameterOverrideValue: string) => {
    const newParameter = merge({}, parameter, {
      overrideValue: parameterOverrideValue,
    });

    onParameterUpdate?.(newParameter);
    closeAndRefocus();
  };

  const closeAndRefocus = () => {
    setOpen(false);
    toggleRef.current?.focus();
  };

  return (
    <div className="filter-bar-filter parameter-item">
      <label className="filter-control-label">
        {parameter.dataType !== SoQLType.SoQLBooleanT && parameter.displayName}
      </label>
      <div
        className="filter-control-container"
        id={pendoIds?.filterParameterControlContainer}
        data-testid="filter-bar-parameter-control-container"
      >
        <ParameterControl onUpdate={onUpdate} />
        {open && (
          <ForgePopover open placement="bottom-start" anchorElement={toggleRef.current}>
            <ParameterControlPopover onUpdate={onUpdate} onClose={closeAndRefocus} />
          </ForgePopover>
        )}
      </div>
    </div>
  );
}

export type ParamItemProps = {
  parameter: ParameterConfiguration;
  clientContextVariables?: DataSourceCCVs;
  editMode?: boolean;
  disabled?: boolean;
  onParameterUpdate?: (parameter: ParameterConfiguration) => void;
  pendoIds?: FilterBarPendoIds;
};

export function ParameterItem({ editMode = false, disabled = false, pendoIds, ...rest }: ParamItemProps) {
  const [open, setOpen] = useState(false);
  const textInputRef = useRef<HTMLSpanElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);

  return (
    <ParameterItemContext.Provider
      value={{
        editMode,
        disabled,
        open,
        textInputRef,
        toggleRef,
        setOpen,
        ...rest,
      }}
    >
      <ParameterItemCore pendoIds={pendoIds} />
    </ParameterItemContext.Provider>
  );
}
